import { Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface PlanGateProps {
  /** When true the children are rendered as-is. When false the upgrade overlay is shown. */
  allowed: boolean;
  /** Display name of the locked feature, e.g. "Webhooks". */
  featureName: string;
  children: ReactNode;
}

/**
 * Wraps a feature section.  When the user's plan does not include the feature
 * a blurred preview is shown beneath an upgrade CTA overlay.
 */
export function PlanGate({ allowed, featureName, children }: PlanGateProps) {
  if (allowed) return <>{children}</>;

  return (
    <div className="relative mb-6">
      {/* Blurred children act as a low-fidelity preview */}
      <div className="pointer-events-none select-none opacity-25 blur-sm" aria-hidden="true">
        {children}
      </div>

      {/* Upgrade overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white/80 backdrop-blur-[2px]">
        <Lock size={28} className="text-brand-500 mb-3" />
        <p className="text-sm font-semibold text-gray-800 mb-1">{featureName} is a Pro feature</p>
        <p className="text-sm text-gray-500 mb-5 text-center max-w-xs px-4">
          Upgrade to unlock {featureName.toLowerCase()} and other advanced tools.
        </p>
        <Link
          to="/profile"
          className="inline-flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          Upgrade to Pro →
        </Link>
      </div>
    </div>
  );
}
